import type { ReactNode } from 'react';
import { Fragment } from 'react';
import { useTranslate } from 'react-polyglot';
import { useTheme } from 'styled-components';

import { RoundedButton } from '@genially/design-system';
import { CloseIcon } from '@genially/icons';

import type { SidebarSide } from './Sidebar.styled';
import { Content, Header, InnerWrapper, Overlay, SlideWrapper } from './Sidebar.styled';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Defaults to the left, which is where every sidebar but the results panel lives. */
  side?: SidebarSide;
  width?: string;
  /**
   * Replaces the default header (a lone close button on the right) when a panel needs its
   * own — the results panel puts the close on the left and a title in the middle.
   */
  header?: ReactNode;
  /**
   * The overlay dims the genially and closes the sidebar on click. The results panel opts
   * out: the host has to be able to keep presenting and resolving questions with the
   * results in view.
   */
  hideOverlay?: boolean;
}

export const Sidebar = ({
  isOpen,
  onClose,
  children,
  side = 'left',
  width,
  header,
  hideOverlay = false,
}: SidebarProps) => {
  const { size } = useTheme();

  const t = useTranslate();

  return (
    <Fragment>
      <SlideWrapper $isOpen={isOpen} $side={side} $width={width ?? size.units(35.75)}>
        <InnerWrapper>
          {header ?? (
            <Header>
              <RoundedButton
                Icon={<CloseIcon />}
                variant={RoundedButton.Variant.PRIMARY}
                size={RoundedButton.Size.SMALL}
                onClick={onClose}
                aria-label={t('__new.play.analyticsResults.close')}
              />
            </Header>
          )}

          <Content>{children}</Content>
        </InnerWrapper>
      </SlideWrapper>

      {!hideOverlay && <Overlay $isOpen={isOpen} onClick={onClose} />}
    </Fragment>
  );
};
